import { useEffect, useState } from "react";
import api, { eur, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Receipt, FileText, Clock } from "lucide-react";
import { toast } from "sonner";

const PAY = [["hotovost", "Hotovosť"], ["karta", "Platobná karta"], ["prevod", "Bankový prevod"]];
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const lineNet = (it) => Number(it.qty || 0) * Number(it.unit_price || 0) * (1 - Number(it.discount || 0) / 100);
export const jobNetTotal = (j) => (j?.items || []).reduce((s, it) => s + lineNet(it), 0);
export const jobGrossTotal = (j) => (j?.items || []).reduce((s, it) => s + lineNet(it) * (1 + (j.vat_mode === "RC" ? 0 : Number(it.vat_rate || 0)) / 100), 0);

export function InternalDocDialog({ job, open, onOpenChange, onDone }) {
  const [withVat, setWithVat] = useState(true);
  const [pay, setPay] = useState("hotovost");
  const [paidDate, setPaidDate] = useState(today());
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setWithVat(jobGrossTotal(job) > jobNetTotal(job) + 0.005); setPay("hotovost"); setPaidDate(today()); } }, [open, job]);
  const amount = withVat ? jobGrossTotal(job) : jobNetTotal(job);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/internal-docs", { job_id: job.id, with_vat: withVat, payment_method: pay, paid_date: paidDate });
      toast.success(`Interný doklad ${data.number} vystavený`);
      onOpenChange(false);
      onDone?.(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="internal-doc-dialog">
        <DialogHeader><DialogTitle className="font-heading uppercase">Interný doklad · {job?.number}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">Doklad vyradí diely zo skladu, zapíše tržbu a označí zákazku ako uhradenú. Nie je daňový doklad.</p>
          <div className="space-y-1.5"><Label>Zákazník zaplatil</Label>
            <div className="flex rounded-lg border border-border p-0.5">
              <button type="button" onClick={() => setWithVat(true)} className={`flex-1 rounded px-3 py-2 text-sm font-semibold ${withVat ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="internal-doc-with-vat">s DPH</button>
              <button type="button" onClick={() => setWithVat(false)} className={`flex-1 rounded px-3 py-2 text-sm font-semibold ${!withVat ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="internal-doc-without-vat">bez DPH</button>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Spôsob úhrady</Label>
              <Select value={pay} onValueChange={setPay}>
                <SelectTrigger data-testid="internal-doc-payment"><SelectValue /></SelectTrigger>
                <SelectContent>{PAY.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Dátum úhrady</Label><Input type="date" value={paidDate} onChange={(e) => setPaidDate(e.target.value)} data-testid="internal-doc-paid-date" /></div>
          </div>
          <div className="rounded-lg border border-violet-500/30 bg-violet-500/10 p-3 text-sm" data-testid="internal-doc-amount">
            Suma dokladu: <b className="font-mono text-lg">{eur(amount)}</b> <span className="text-xs text-muted-foreground">{withVat ? "s DPH" : "bez DPH"}</span>
            {withVat && <div className="text-xs text-muted-foreground">Tržba bez DPH: {eur(jobNetTotal(job))}</div>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Zrušiť</Button>
          <Button onClick={submit} disabled={busy || !job} className="gap-2" data-testid="internal-doc-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Receipt className="h-4 w-4" />} Vystaviť doklad</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PaidPromptDialog({ job, onClose, onInternal, onInvoice, onSkip }) {
  return (
    <Dialog open={!!job} onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent className="sm:max-w-md" data-testid="paid-prompt-dialog">
        <DialogHeader><DialogTitle className="font-heading uppercase">Prajete si vystaviť interný doklad?</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Zákazka {job?.number} zatiaľ nemá doklad. Označením ako uhradená sa diely vyradia zo skladu.</p>
        <div className="flex flex-col gap-2">
          <Button className="gap-2" onClick={onInternal} data-testid="paid-prompt-internal"><Receipt className="h-4 w-4" /> Vystaviť interný doklad</Button>
          <Button variant="secondary" className="gap-2" onClick={onInvoice} data-testid="paid-prompt-invoice"><FileText className="h-4 w-4" /> Vystaviť faktúru</Button>
          <Button variant="ghost" className="gap-2" onClick={onSkip} data-testid="paid-prompt-skip"><Clock className="h-4 w-4" /> Teraz nie</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DocBadge({ doc, testid }) {
  if (!doc) return <span className="rounded border border-border px-2 py-0.5 text-[10px] font-bold uppercase text-muted-foreground" data-testid={testid}>Bez dokladu</span>;
  const internal = doc.kind === "internal";
  return <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase ${internal ? "bg-violet-500/15 text-violet-300" : "bg-sky-500/15 text-sky-300"}`} data-testid={testid}>{internal ? "Interný doklad" : "Faktúra"} {doc.number}</span>;
}
